"use client";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { changeSkills, selectSkills } from "../../lib/redux/resumeSlice";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Star } from "lucide-react";

export const SkillsForm = () => {
  const dispatch = useAppDispatch();
  const skills = useAppSelector(selectSkills);

  const handleFeaturedSkillChange = (idx: number, skill: string) => {
    dispatch(changeSkills({ field: "featuredSkills", idx, skill, rating: skills.featuredSkills[idx].rating }));
  };

  const handleFeaturedSkillRatingChange = (idx: number, rating: number) => {
    dispatch(changeSkills({ field: "featuredSkills", idx, skill: skills.featuredSkills[idx].skill, rating }));
  };

  const handleSkillsDescriptionsChange = (descriptions: string[]) => {
    dispatch(changeSkills({ field: "descriptions", value: descriptions }));
  };

  return (
    <Card className="bg-white border-2 border-gray-800 shadow-lg">
      <CardHeader className="bg-gray-900 text-white">
        <CardTitle className="text-lg font-semibold">Skills</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Featured Skills */}
        <div className="space-y-4">
          <Label className="text-base font-semibold text-black">Featured Skills</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {skills.featuredSkills.map((skill, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`skill-${idx}`} className="text-sm font-semibold text-black">Skill {idx + 1}</Label>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleFeaturedSkillRatingChange(idx, rating)}
                        className={`p-1 rounded ${
                          skill.rating >= rating ? "text-yellow-500" : "text-gray-300"
                        } hover:text-yellow-400 transition-colors`}
                      >
                        <Star className="h-4 w-4 fill-current" />
                      </button>
                    ))}
                  </div>
                </div>
                <Input
                  id={`skill-${idx}`}
                  value={skill.skill}
                  onChange={(e) => handleFeaturedSkillChange(idx, e.target.value)}
                  placeholder="JavaScript"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Additional Skills */}
        <div className="space-y-2">
          <Label htmlFor="skills-descriptions" className="text-black font-semibold">Additional Skills & Certifications</Label>
          <Textarea
            id="skills-descriptions"
            value={skills.descriptions.join('\n')}
            onChange={(e) => {
              const descriptions = e.target.value.split('\n').filter(line => line.trim());
              handleSkillsDescriptionsChange(descriptions);
            }}
            placeholder="• AWS Certified Solutions Architect
• Certified Scrum Master (CSM)
• Fluent in Spanish and French
• Advanced Excel and Data Analysis"
            rows={4}
            className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors resize-none"
          />
          <p className="text-sm text-gray-600">
            Enter each skill or certification on a new line starting with a bullet point (•)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
